import { useEffect, useState, useCallback } from 'react';
import Swal from 'sweetalert2';
import API_CIFRA from '../../config/Variables_apis';
import { tokenManager } from '../../config/authConfig';
import PanelShell from './PanelShell';
import '../../styles/Panel.css';

const ESTADOS = [
  { valor: 'publicado', etiqueta: 'Publicar' },
  { valor: 'pendiente', etiqueta: 'Pendiente' },
  { valor: 'oculto', etiqueta: 'Ocultar' },
  { valor: 'bloqueado', etiqueta: 'Bloquear' },
];

const cabecerasJson = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${tokenManager.getAccessToken()}`,
});

/**
 * Moderación de comentarios ciudadanos (panel). Además de cambiar el estado,
 * permite ver/alternar la visibilidad del archivo (público o privado) y
 * bloquear por IP —guardando la IP como dato básico— a quien incumple las
 * políticas de uso.
 */
const GestionComentarios = () => {
  const [comentarios, setComentarios] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [cargando, setCargando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const url = filtro
        ? `${API_CIFRA.incidencia.comentariosPanel}?estado=${filtro}`
        : API_CIFRA.incidencia.comentariosPanel;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` } });
      if (r.ok) setComentarios(await r.json());
    } catch {
      /* silencioso: se muestra lista vacía */
    } finally {
      setCargando(false);
    }
  }, [filtro]);

  useEffect(() => { cargar(); }, [cargar]);

  const moderar = async (comentario, cambios) => {
    try {
      const r = await fetch(API_CIFRA.incidencia.comentarioModerar(comentario.com_inId), {
        method: 'PATCH',
        headers: cabecerasJson(),
        body: JSON.stringify(cambios),
      });
      if (r.ok) {
        cargar();
      } else {
        Swal.fire({ icon: 'error', title: 'No se pudo moderar', text: 'Intenta de nuevo.' });
      }
    } catch {
      Swal.fire({ icon: 'error', title: 'Error de conexión', text: 'Intenta de nuevo.' });
    }
  };

  const bloquearIP = async (comentario) => {
    if (!comentario.ip) return;
    const { isConfirmed, value } = await Swal.fire({
      title: 'Bloquear por IP',
      html: `Se bloqueará el acceso desde <b>${comentario.ip}</b>.`,
      input: 'text',
      inputPlaceholder: 'Motivo (ej. comentarios ofensivos)',
      showCancelButton: true,
      confirmButtonText: 'Bloquear',
      cancelButtonText: 'Cancelar',
    });
    if (!isConfirmed) return;
    try {
      const r = await fetch(API_CIFRA.configuracion.bloqueos, {
        method: 'POST',
        headers: cabecerasJson(),
        body: JSON.stringify({
          blo_chIp: comentario.ip,
          blo_txMotivo: (value || '').trim() || 'Viola las políticas de uso',
        }),
      });
      if (r.ok) {
        Swal.fire({ icon: 'success', title: 'IP bloqueada', text: comentario.ip, timer: 1800, showConfirmButton: false });
      } else {
        Swal.fire({ icon: 'error', title: 'No se pudo bloquear la IP' });
      }
    } catch {
      Swal.fire({ icon: 'error', title: 'Error de conexión' });
    }
  };

  return (
    <PanelShell>
      <div className="panel-gu-cabecera">
        <div>
          <h1>Comentarios</h1>
          <p className="panel-texto-ayuda">
            Modera los comentarios de los vecinos. Los archivos privados solo se ven aquí;
            puedes cambiar su visibilidad o bloquear la IP de quien incumpla las políticas.
          </p>
        </div>
      </div>

      <div className="panel-gu-filtros">
        <select
          className="panel-input"
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          style={{ maxWidth: 240 }}
        >
          <option value="">Todos los estados</option>
          {ESTADOS.map((e) => (
            <option key={e.valor} value={e.valor}>{e.etiqueta}</option>
          ))}
        </select>
      </div>

      {cargando && <p className="panel-texto-ayuda">Cargando…</p>}
      {!cargando && comentarios.length === 0 && (
        <p className="panel-texto-ayuda">No hay comentarios para mostrar.</p>
      )}

      <div className="panel-comentarios-lista">
        {comentarios.map((c) => (
          <div key={c.com_inId} className="panel-comentario-item">
            <div className="panel-comentario-cabecera">
              <strong>{c.com_chAlias || 'Vecino'}</strong>
              <span className="panel-texto-ayuda">
                Ticket {c.ticket} · {new Date(c.com_dtFechaCreated).toLocaleString('es-PE')}
              </span>
            </div>
            <p className="panel-comentario-texto">{c.com_txTexto}</p>

            {c.com_imgFoto && (
              <div className="panel-comentario-adjunto">
                <a href={c.com_imgFoto} target="_blank" rel="noreferrer">
                  <img src={c.com_imgFoto} alt="Adjunto" className="panel-comentario-foto" />
                </a>
                <span className={`panel-comentario-badge-archivo${c.com_boArchivoPublico === false ? ' es-privado' : ''}`}>
                  <i className={`fas ${c.com_boArchivoPublico === false ? 'fa-lock' : 'fa-eye'}`}></i>
                  {c.com_boArchivoPublico === false ? 'Privado' : 'Público'}
                </span>
              </div>
            )}

            <div className="panel-comentario-acciones">
              <span className={`panel-comentario-estado panel-comentario-estado-${c.com_chEstado}`}>
                {c.estado_display}
              </span>
              {ESTADOS.filter((e) => e.valor !== c.com_chEstado).map((e) => (
                <button
                  key={e.valor}
                  type="button"
                  className="panel-boton-secundario"
                  onClick={() => moderar(c, { com_chEstado: e.valor })}
                >
                  {e.etiqueta}
                </button>
              ))}
              {c.com_imgFoto && (
                <button
                  type="button"
                  className="panel-boton-secundario"
                  onClick={() => moderar(c, { com_boArchivoPublico: !(c.com_boArchivoPublico !== false) })}
                >
                  {c.com_boArchivoPublico === false ? 'Hacer público' : 'Hacer privado'}
                </button>
              )}
              {c.ip && (
                <button
                  type="button"
                  className="panel-boton-secundario panel-boton-peligro"
                  onClick={() => bloquearIP(c)}
                  title={`IP ${c.ip}`}
                >
                  <i className="fas fa-ban"></i> Bloquear IP ({c.ip})
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </PanelShell>
  );
};

export default GestionComentarios;
